import { Selection, EnterElement, axisTop, ScaleTime, D3DragEvent, drag, DragBehavior } from "d3"
import { D3DragOverlay } from "./D3DragOverlay"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import { getTimezoneOffset } from "date-fns-tz"

export type D3UTCAxisConfig = {
	viewScale: ScaleTime<any, any, any>
	fileScale: ScaleTime<any, any, any>
	liveModeEnabled: boolean
	timeZone: string
	onDragStart? (dragEvent: D3DragEvent<any, any, any>): void
	onDrag? (dragEvent: D3DragEvent<any, any, any>): void
	onDragEnd? (dragEnd: D3DragEvent<any, any, any>): void
}

export const MIN_WINDOW_TIME_MS = 1000

export class D3UTCAxis extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3UTCAxisConfig> {
	private dragOverlayHeight: number = 30
	private dragOverlayHorizontalExtension: number = 10
	private dragBehavior: DragBehavior<any, any, any>
	private context: CanvasRenderingContext2D | undefined = undefined
	private superSamplingRatio: number = 2 // supersampling avoids blurry text when the canvas is placed on a subpixel.
	private height = 20

	// Children
	private dragOverlay?: D3DragOverlay

	private colorSchemeObserver: MutationObserver

	constructor(root: SVGGElement, config: D3UTCAxisConfig, reactCallbacks: ReactCallbacks<any>) {
		super(root, config, "d3-utc-axis", reactCallbacks)

		this.dragBehavior = drag()
			.on("start", this.onDragStart)
			.on("drag", this.onDrag)
			.on("end", this.onDragEnd)

		this.colorSchemeObserver = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.attributeName === "data-color-scheme") {
					this.render()
					break
				}
			}
		})
		this.colorSchemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-color-scheme"] })

		this.render()
	}

	public destroy() {
		this.colorSchemeObserver.disconnect()
	}

	public enter = (newAxis: Selection<EnterElement, any, any, any>): Selection<SVGGElement, any, any, any> => {
		// Create the Axis Group
		const width = this.config.viewScale?.range()[1] - this.config.viewScale?.range()[0]

		const axisGroup = newAxis
			.append("g")
			.attr("class", this.className)

		const foreignObject = axisGroup
			.append("foreignObject")
			.attr("transform", "translate(0,-20)")
		
		const canvas = foreignObject.append("xhtml:canvas") as Selection<HTMLCanvasElement, any, any, any>

		foreignObject
			.attr("width", width)
			.attr("height", this.height)
		
		canvas
			.attr("width", width * this.superSamplingRatio)
			.attr("height", (this.height+1) * this.superSamplingRatio) // +1 for drawing the bottom axis line
			.style("width", `${width}px`)
			.style("height", `${this.height}px`)

		const canvasNode = canvas.node()

		if (canvasNode != null) {
			this.context = canvasNode.getContext("2d") ?? undefined
		}

		this.drawAxisTop()

		axisGroup.each(this.createChildren)

		return axisGroup 
	}

	public update = (updatedAxis: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const foreignObject = updatedAxis.select("foreignObject")
		const canvas = foreignObject.select("canvas")
		const width = this.config.viewScale?.range()[1] - this.config.viewScale?.range()[0]

		foreignObject.attr("width", width)

		canvas
			.attr("width", width * this.superSamplingRatio)
			.style("width", `${width}px`)

		this.drawAxisTop()
		this.renderChildren()

		return updatedAxis
	}

	protected updateDerivedState(): void {
		this.updateChildren()
	}

	private onDragStart = (dragEvent: D3DragEvent<any, any, any>) => {
		if (this.config.liveModeEnabled) {
			return
		}

		if (this.config.onDragStart) {
			this.config.onDragStart(dragEvent)
		}
	}

	private onDrag = (dragEvent: D3DragEvent<any, any, any>) => {
		if (this.config.liveModeEnabled) {
			return
		}
		
		const startTime = this.config.viewScale.domain()[0].getTime()
		const endTime = this.config.viewScale.domain()[1].getTime()
		const recordingEndTime = this.config.fileScale.domain()[1].getTime()
		
		const width = this.config.viewScale.range()[1] - this.config.viewScale.range()[0]

		const range = endTime - startTime

		const sensitivity = range / width
		let { dx, dy } = dragEvent

		const pointerX = dragEvent.x - dx
		const zoomScaling = pointerX / width // keep zooming centered on cursor

		const startTimeZoomChange = 4 * dy * sensitivity * zoomScaling
		const endTimeZoomChange = -4 * dy * sensitivity * (1 - zoomScaling)

		let newStartTime = startTime + -dx * sensitivity + startTimeZoomChange
		let newEndTime = endTime + -dx * sensitivity + endTimeZoomChange

		if (newEndTime - newStartTime < MIN_WINDOW_TIME_MS && dy > 0) {
			newStartTime -= startTimeZoomChange
			newEndTime -= endTimeZoomChange
		} 
		
		if (newEndTime > recordingEndTime) {
			newEndTime = recordingEndTime
		} 
		
		this.config.viewScale.domain([newStartTime, newEndTime])

		if (this.config.onDrag) {
			this.config.onDrag(dragEvent)
		}

		requestAnimationFrame(() => {
			this.render()
		})
	}

	private onDragEnd = (dragEvent: D3DragEvent<any, any, any>) => {
		if (this.config.liveModeEnabled) {
			return
		}
		
		if (this.config.onDragEnd) {
			this.config.onDragEnd(dragEvent)

			// we need to force re-render the react components
			this.reactCallbacks.setRootConfig((previous: any) => ({...previous}))
		}
	}

	protected createChildren = (config: D3UTCAxisConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		const width = config.viewScale.range()[1]
		const boundingBox = {
			x: -this.dragOverlayHorizontalExtension,
			y: -this.dragOverlayHeight,
			width: width + 2 * this.dragOverlayHorizontalExtension,
			height: this.dragOverlayHeight,
		}

		if (this.config.onDrag || this.config.onDragStart) {
			this.dragOverlay = new D3DragOverlay(root, { boundingBox, dragBehavior: this.dragBehavior, cursor: this.config.liveModeEnabled ? "default" : "zoom-in" }, this.reactCallbacks)
		}
	}

	protected renderChildren = () => {
		this.dragOverlay?.render()
	}

	protected updateChildren = () => {
		const width = this.config.viewScale.range()[1]
		const boundingBox = {
			x: -this.dragOverlayHorizontalExtension,
			y: -this.dragOverlayHeight,
			width: width + 2 * this.dragOverlayHorizontalExtension,
			height: this.dragOverlayHeight,
		}
		this.dragOverlay?.updateConfig({ boundingBox, dragBehavior: this.dragBehavior, cursor: this.config.liveModeEnabled ? "default" : "zoom-in" })
	}

	private getTimeZoneShiftedScale() {
		const [startDate, endDate] = this.config.viewScale.domain()
		const startTime = startDate.getTime() + getTimezoneOffset(this.config.timeZone, startDate)
		const endTime = endDate.getTime() + getTimezoneOffset(this.config.timeZone, endDate)
		return this.config.viewScale.copy().domain([startTime, endTime])
	}

	private drawAxisTop() {
		// The reason we're using a canvas instead of SVG is because rendering the axis every frame causes the text nodes to 
		// be recreated every frame. Not only is that memory intensive, it causes a bunch of garbage collection and frame drops.
		// With Canvas, no DOM updates are happening, which is much nicer on the CPU and prevents DOM node garbage collection.

		if (!this.context) {
			return
		}

		const height = this.height * this.superSamplingRatio
		const tickHeight = 6 * this.superSamplingRatio
		const scale = this.getTimeZoneShiftedScale()
		const width = (scale.range()[1] - scale.range()[0]) * this.superSamplingRatio
		const d3Axis = axisTop(scale)
		const tickArguments = d3Axis.tickArguments()
		const ticks = scale.ticks(...(tickArguments ?? []))
		const format = scale.tickFormat?.(ticks.length) ?? (d => d.toString())

		// Clear
		this.context.clearRect(0, 0, width, height)

		// Set Properties
		this.context.font = `${0.8 * this.superSamplingRatio}em sans-serif`
		this.context.textAlign = "center"
		this.context.textBaseline = "bottom"
		const axisColor = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black"
		this.context.fillStyle = axisColor
		this.context.strokeStyle = axisColor
		this.context.lineWidth = 1 * this.superSamplingRatio
	
		// Axis line
		this.context.beginPath()
		this.context.moveTo(0, height)
		this.context.lineTo(width, height)
		this.context.stroke()

		// First Tick
		this.context.beginPath()
		this.context.moveTo(0, height)
		this.context.lineTo(0, height - tickHeight)
		this.context.stroke()

		// Last Tick
		this.context.beginPath()
		this.context.moveTo(width, height)
		this.context.lineTo(width, height - tickHeight)
		this.context.stroke()

		// In-between Ticks
		for (const tick of ticks) {
			const x = scale(tick) * this.superSamplingRatio
			this.context.beginPath()
			this.context.moveTo(x, height)
			this.context.lineTo(x, height - tickHeight)
			this.context.stroke()
			this.context.fillText(format(tick), x, (height - tickHeight))
		}
	}
}
